// src/components/DropZone.tsx
import React, { useState } from 'react';
import { useElementStore } from '../store/elementStore';
import ElementCanvas from './ElementCanvas';
import MobileElementSelector from './MobileElementSelector';

interface DropZoneProps {
  zoneIndex: number;
  customSelectHandler?: (id: string, zoneIndex: number) => void;
}

// src/components/DropZone.tsx
export default function DropZone({ zoneIndex, customSelectHandler }: DropZoneProps) {
  const selected = useElementStore(s => s.selected);
  const select = useElementStore(s => s.select);
  const all = useElementStore(s => s.all);

  const [hover, setHover] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const currentId = selected[zoneIndex];

  // Handle element selection - use custom handler if provided
  const handleSelect = (id: string) => {
    if (customSelectHandler) {
      customSelectHandler(id, zoneIndex);
    } else {
      select(id, zoneIndex);
    }
  };

  // Handle clearing the zone
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(''); // Empty string clears the selection
  };

  return (
    <>
      <div
        className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center transition-colors drop-zone
          ${hover
            ? 'bg-gray-600 border-2 border-blue-400'
            : currentId
              ? 'bg-gray-700'
              : 'bg-gray-700 border-2 border-dashed border-gray-500'
          }`}
        data-zone-id={`zone-${zoneIndex}`}
        onDragOver={e => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={e => {
          e.preventDefault();
          setHover(false);
          const id = e.dataTransfer.getData('text/plain');
          if (id) handleSelect(id);
        }}
        // Add click handler for mobile
        onClick={() => {
          if (!currentId) {
            setShowSelector(true);
          }
        }}
      >
        {currentId ? (
          <div className="relative">
            <ElementCanvas e={all[currentId]!} size={48} />
            <button
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full text-white flex items-center justify-center"
              onClick={handleClear}
            >
              ×
            </button>
          </div>
        ) : (
          <span className="text-gray-400 text-center text-xs px-2">Tap to choose element</span>
        )}
      </div>
      <div className="mt-2 text-center text-xs sm:text-sm text-gray-300">
        Slot {zoneIndex + 1}
      </div>

      {/* Element selector modal for mobile */}
      {showSelector && (
        <MobileElementSelector
          onSelect={handleSelect}
          onClose={() => setShowSelector(false)}
          currentSlot={zoneIndex}
        />
      )}
    </>
  );
}